import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { DollarSign, Users, AlertTriangle, TrendingUp, MessageSquare, CalendarDays, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export default function Dashboard() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  // If user signed up via a pricing CTA, auto-send them to Stripe Checkout once dashboard loads
  useEffect(() => {
    const pendingTier = localStorage.getItem('pending_stripe_tier');
    if (pendingTier) {
      localStorage.removeItem('pending_stripe_tier');
      navigate(`/settings?auto_checkout=${pendingTier}`, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    async function load() {
      try {
        const ax = api();
        const [kRes, cRes, fRes] = await Promise.all([
          ax.get('/api/dashboard/kpis'),
          ax.get('/api/dashboard/revenue-chart'),
          ax.get('/api/dashboard/funnel'),
        ]);
        setKpis(kRes.data);
        setChartData(cRes.data);
        setFunnel(fRes.data);
      } catch (e) {
        console.error('Dashboard load error', e);
      }
      setLoading(false);
    }
    load();
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const kpiCards = [
    { label: 'Potential Revenue Leaks', value: `$${(kpis?.potential_leak_value || 0).toLocaleString()}`, icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', link: '/revenue-leaks' },
    { label: 'Recovered Revenue', value: `$${(kpis?.recovered_revenue || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'Won Revenue', value: `$${(kpis?.won_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-slate-900', bg: 'bg-slate-100' },
    { label: 'Total Leads', value: kpis?.total_leads || 0, icon: Users, color: 'text-blue-500', bg: 'bg-blue-50', link: '/leads' },
    { label: 'New Leads (30d)', value: kpis?.new_leads_30d || 0, icon: ArrowUpRight, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'No Shows', value: kpis?.no_shows || 0, icon: ArrowDownRight, color: 'text-red-500', bg: 'bg-red-50' },
    { label: 'SMS Sent', value: kpis?.sms_sent || 0, icon: MessageSquare, color: 'text-violet-500', bg: 'bg-violet-50' },
    { label: 'Appointments Today', value: kpis?.appointments_today || 0, icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-50', link: '/appointments' },
  ];

  const funnelData = funnel ? Object.entries(funnel).map(([stage, data]) => ({
    stage: stage.replace(/_/g, ' '),
    count: data.count,
    value: data.value,
  })) : [];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Revenue recovery overview</p>
        </div>
        <Link to="/import-data" data-testid="dashboard-import-btn" className="text-sm font-medium bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-all">
          Import Data
        </Link>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpiCards.map((kpi) => {
          const Card = kpi.link ? Link : 'div';
          return (
            <Card
              key={kpi.label}
              to={kpi.link || '#'}
              data-testid={`kpi-${kpi.label.toLowerCase().replace(/[\s()]/g, '-')}`}
              className="bg-white rounded-xl border border-slate-200 p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 ${kpi.bg} rounded-lg flex items-center justify-center`}>
                  <kpi.icon size={18} className={kpi.color} />
                </div>
                {kpi.link && <ArrowUpRight size={14} className="text-slate-300" />}
              </div>
              <p className="text-2xl font-bold text-slate-900 tracking-tight">{kpi.value}</p>
              <p className="text-xs text-slate-500 mt-1">{kpi.label}</p>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Revenue (30 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94A3B8" tickFormatter={v => `$${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                  formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#0F172A" fill="#0F172A" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pipeline Funnel */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Pipeline Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis dataKey="stage" type="category" width={110} tick={{ fontSize: 9 }} stroke="#94A3B8" />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                  formatter={(v, name) => [name === 'count' ? v : `$${v.toLocaleString()}`, name === 'count' ? 'Leads' : 'Value']}
                />
                <Bar dataKey="count" fill="#0F172A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
